import React, { useEffect, useRef, useState } from "react";
import AppTable from "../../AppTable/AppTable";
import { useDispatch, useSelector } from "react-redux";
import {
  createPart,
  deletePart,
  exportCSV,
  fetchAllParts,
  updatePart,
  uploadCSV,
} from "../../../redux/partSlice";
import { getAllUnits } from "../../../redux/unitSlice";
import FilterDropdown from "./FilterDropdown";
import { scrollToTop } from "../../../util/AppUtils";
import { showSnackbar } from "../../../redux/snackbarSlice";
import "./PartsTable.css";
import DeleteConfirmation from "../../Confirmations/DeleteConfirmation";
import TypesPopup from "../Types/TypesPopup";
import AddBtn from "../AddBtn";
import UploadViaCsvBtn from "../UploadViaCsvBtn";
import ExportCsvBtn from "../ExportCsvBtn";
import { GetAuditTrailForImport } from "../../../util/getAuditTrailData";
import ImportCsvSuccessPopup from "../../ImportCsvSuccessPopup";
import Loader from "../../Loader";
import ColorTextEditor2 from "./ColorTextEditor2";
import CustomColorPicker2 from "./CustomColorPicker2";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import PlaygroundNodes from "../../RichTextEditor/LexicalEditor/nodes/PlaygroundNodes";
import PlaygroundEditorTheme from "../../RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme";
import { buildImportMap } from "../../../util/lexical";
import TypesDropdown from "../Types/TypesDropdown";
import UnitesDropdown from "./UnitesDropdown";
import {
  TableActionGroup,
  EditRowButton,
  DeleteRowButton,
  SelectRowButton,
} from "../../Buttons";
import PartMentionsPopup from "./PartMentionsPopup";

const PartsTable = ({
  onSelectPart,
  disableFilter = false,
  disableActions = false,
  items_type = "all",
  checkRevCreated,
  checkRevReleased,
  check_module_created,
  checkRevCreatedNot,
  useTableInAuditTrail = false,
  auditTrailRow,
  disableBomOptions = false,
  onCloseParentPopup = null,
  customQueryParams = "",
  disableActionButtons = false,
  onPartUpdated = null,
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filterType, setFilterType] = useState(items_type);
  const [whereUsedPart, setWhereUsedPart] = useState(null);

  // Create separate editor instances for each cell
  const createEditorConfig = (editorId) => ({
    namespace: `Playground_${editorId}`,
    nodes: [...PlaygroundNodes],
    html: { import: buildImportMap() },
    onError: (error) => {
      console.error(`Error occurred in Playground ${editorId}:`, error);
    },
    theme: PlaygroundEditorTheme,
  });

  // Common
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const tableContainerRef = useRef();

  const columns = [
    { headName: "Where Mentioned?", field: "where_used" },
    {
      headName: "Type",
      field: "name",
      sort: true,
      filter: true,
      required: true,
      width: "150px",
    },
    {
      headName: "Item Code",
      field: "part_number",
      sort: true,
      filter: true,
      required: true,
    },
    {
      headName: "Description",
      field: "description",
      sort: true,
      filter: true,
      width: "150px",
    },
    {
      headName: "Table Note",
      field: "table_note",
      sort: true,
      filter: true,
      width: "150px",
    },
    {
      headName: "Text Box Note",
      field: "textbox_note",
      sort: true,
      filter: true,
      width: "150px",
    },
    {
      headName: "Default Unit",
      field: "unit_name",
      sort: true,
      filter: true,
      width: "95px",
    },
    { headName: "URL", field: "url", sort: true, filter: true },
    { headName: "Action", field: "action", textCenter: true, isAction: true },
  ];
  const dispatch = useDispatch();
  const {
    parts,
    pagination,
    disabledFilters,
    procedure_rev_ref_id,
    part_ref_id,
    step_tree_node_id,
  } = useSelector((state) => state.parts);

  // Use ref to track if this is the initial mount with customQueryParams
  const customQueryParamsInitializedRef = useRef(false);
  
  useEffect(() => {
    let fltrType = null;
    if (!useTableInAuditTrail) {
      // For bom_step filter, wait until step_tree_node_id is available
      if (filterType === "bom_step" && step_tree_node_id == null) {
        // Don't make API call yet if step ID is not available
        return;
      }

      // For bom_procedure filter, wait until procedure_rev_ref_id is available
      if (filterType === "bom_procedure" && procedure_rev_ref_id == null) {
        // Don't make API call yet if procedure rev ID is not available
        return;
      }

      // If customQueryParams is provided but searchQuery is empty, skip the immediate call
      // Let AppTable's onFilterSortHandler build the full query first
      if (customQueryParams && !searchQuery && !customQueryParamsInitializedRef.current) {
        customQueryParamsInitializedRef.current = true;
        return;
      }

      // Always build the query from the current table state
      let query = searchQuery || "";

      if (filterType !== "all" && filterType !== undefined) {
        if (filterType === "bom_procedure") {
          let parts = ["&is_bom_tbl_part=true"];
          if (procedure_rev_ref_id != null) {
            parts.push(`&procedure_rev_ref_id=${procedure_rev_ref_id}`);
          }
          if (part_ref_id != null) {
            parts.push(`&part_ref_id=${part_ref_id}`);
          }
          fltrType = parts.join("");
        } else if (filterType === "bom_step") {
          let parts = ["&is_bom_tbl_part_step=true"];
          if (step_tree_node_id != null) {
            parts.push(`&step_tree_node_id=${step_tree_node_id}`);
          }
          fltrType = parts.join("");
        } else if (filterType === "is_flowchart") {
          fltrType = `&is_flowchart=true&check_is_released=true`;
        } else {
          fltrType = `&${filterType}=true`;
        }
      }

      if (fltrType) {
        query += fltrType;
      }

      if (check_module_created) {
        query += "&check_module_created=true";
      }
      if (checkRevReleased) {
        query += "&getpartrev=true";
      }

      // Add custom query params if provided (e.g., for suggestion rule matches)
      if (customQueryParams) {
        // If query already has content, add & separator, otherwise use customQueryParams as base
        if (query) {
          query += `&${customQueryParams}`;
        } else {
          query = customQueryParams;
        }
      }

      // Persist last query to ensure post add/update refresh works even if user hasn't typed a search
      localStorage.itemCodeSearchQuery = query;
      dispatch(fetchAllParts({ searchQuery: query }));
    }
  }, [
    dispatch,
    searchQuery,
    limit,
    filterType,
    customQueryParams,
    procedure_rev_ref_id,
    part_ref_id,
    step_tree_node_id,
    check_module_created,
    checkRevReleased,
    useTableInAuditTrail,
  ]);

  // Reset the ref when customQueryParams changes
  useEffect(() => {
    customQueryParamsInitializedRef.current = false;
  }, [customQueryParams]);

  // End Common

  const [fieldState, setFieldState] = useState(null);
  const [importedInfo, setImportData] = useState(null);

  const [newRow, setNewRow] = useState(null);
  const [type, setType] = useState("");
  const [typesModalOpen, setTypesModalOpen] = useState(false);
  const { units } = useSelector((state) => state.units);
  const fileInputRef = useRef(null);
  const [newPart, setNewPart] = useState({
    type_id_ref: "",
    type_name: "",
    unit_type_id_ref: "",
    part_number: "",
    description: "",
    table_note: "",
    textbox_note: "",
    default_unit: "",
    default_unit_show: "",
    url: "",
  });
  const handleInputChange = (e) => {
    const { name, value, options, selectedIndex } = e.target;

    setNewPart({ ...newPart, [name]: value });
  };

  const snackbar = (message, type) => {
    dispatch(showSnackbar({ type: type, message: message }));
  };

  const isValidUrl = (urlString) => {
    if (!urlString || typeof urlString !== "string" || !urlString.trim()) {
      return true; // Empty URL is allowed
    }
    const trimmedUrl = urlString.trim();
    try {
      const url = new URL(trimmedUrl);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  const handleAddRow = async () => {
    if (!newPart.type_id_ref || !newPart.part_number.trim()) {
      snackbar("Type and Item Code are required!", "error");
      return;
    }

    // Validate URL if provided
    const urlValue = newPart.url;
    if (urlValue !== undefined && urlValue !== null && urlValue !== "") {
      const trimmedUrl = String(urlValue).trim();
      if (trimmedUrl && !isValidUrl(trimmedUrl)) {
        snackbar("Please enter a valid URL", "error");
        return;
      }
    }

    try {
      setShowLoader(true);
      await dispatch(
        createPart({ ...newPart, part_number: newPart.part_number?.trim() })
      ).then(async (resp) => {
        setShowLoader(false);
        if (resp.payload.success === false) {
          snackbar(resp.payload.error, "error");
          return;
        } else {
          setNewRow(null);
          setNewPart({
            type_id_ref: "",
            type_name: "",
            unit_type_id_ref: "",
            part_number: "",
            description: "",
            table_note: "",
            textbox_note: "",
            default_unit: "",
            default_unit_show: "",
            url: "",
          });
          setType("");
          snackbar("Item code created successfully!", "success");
          dispatch(
            fetchAllParts({ searchQuery: localStorage.itemCodeSearchQuery })
          );
        }
      });
    } catch (error) {
      snackbar("Item code creation failed! " + error?.message, "error");
    }
  };
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedRowData, setEditedRowData] = useState({});
  const handleSaveEdit = async (id) => {
    const partToUpdate = parts.find((p) => p.part_id === id);
    if (!editedRowData.type_id_ref) {
      snackbar("Type is required!", "error");
      return;
    }

    // Validate URL if provided
    if (editedRowData.hasOwnProperty("url")) {
      const urlValue = editedRowData.url;
      if (urlValue != null && String(urlValue).trim() !== "") {
        const trimmedUrl = String(urlValue).trim();
        if (!isValidUrl(trimmedUrl)) {
          snackbar("Please enter a valid URL", "error");
          return;
        }
      }
    }
    try {
      setShowLoader(true);
      await dispatch(
        updatePart({
          id,
          data: {
            ...editedRowData,
            part_number: editedRowData.part_number?.trim(),
          },
          parent_version: partToUpdate?.version_id_ref,
        })
      );
      setShowLoader(false);
      setEditingRowId(null);
      setEditedRowData({});
      setNewRow(null);
      setNewPart({
        type_id_ref: "",
        type_name: "",
        unit_type_id_ref: "",
        part_number: "",
        description: "",
        table_note: "",
        textbox_note: "",
        default_unit: "",
        default_unit_show: "",
        url: "",
      });
      setType("");
      dispatch(
        fetchAllParts({ searchQuery: localStorage.itemCodeSearchQuery })
      );
      if (typeof onPartUpdated === "function") {
        onPartUpdated({
          ...(partToUpdate || {}),
          ...editedRowData,
          part_id: id,
          part_number: editedRowData.part_number?.trim(),
        });
      }
      snackbar("Item code edited successfully", "success");
    } catch (err) {
      snackbar("Part edit failed!", "error");
    }
  };
  const handleDelete = (id) => {
    DeleteConfirmation(async () => {
      try {
        setShowLoader(true);
        await dispatch(deletePart(id)).unwrap();
        setShowLoader(false);
        snackbar("Part deleted successfully!", "success");
        dispatch(
          fetchAllParts({ searchQuery: localStorage.itemCodeSearchQuery })
        );
      } catch (error) {
        setShowLoader(false);
        snackbar(
          error?.message || error?.error || "Part deletion failed!",
          "error"
        );
      }
    }, "Are you sure you want to delete this part?");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset the file input to allow same file upload next time
    e.target.value = "";

    try {
      setShowLoader(true);
      const auditTrailData = await GetAuditTrailForImport();

      try {
        const response = await dispatch(
          uploadCSV({ csvFile: file, auditTrailData })
        );
        setShowLoader(false);
        if(response?.payload?.success){
          snackbar(
            response.payload.message,
            "success"
          );
        }
        if (response?.payload?.success) {
          dispatch(
            fetchAllParts({ searchQuery: localStorage.itemCodeSearchQuery })
          );
        }
        setImportData(response?.payload);
      } catch (error) {
        snackbar(
          "Error importing CSV: " + (error.message || "Unknown error"),
          "error"
        );
      }
    } catch (err) {
      snackbar("Operation failed. Please try again.", "error");
    }
  };

  const handleExportCSV = () => {
    setShowLoader(true);
    dispatch(exportCSV()).then((res) => {
      setShowLoader(false);
      if (exportCSV.fulfilled.match(res)) {
        snackbar("CSV export completed!", "success");
      } else {
        snackbar("Error exporting CSV: " + res.payload, "error");
      }
    });
  };

  const handleSelectType = async (selectedType) => {
    const typ = { name: undefined, type_id: undefined };

    if (selectedType !== null) {
      typ.name = selectedType.label;
      typ.type_id = selectedType.value;
    }

    if (editingRowId) {
      setEditedRowData((prev) => ({
        ...prev,
        type_name: typ.name,
        type_id_ref: typ.type_id,
      }));
    } else {
      setType(typ.name || "");
      setNewPart((prev) => ({
        ...prev,
        type_name: typ.name,
        type_id_ref: typ.type_id,
      }));
    }
    // setTypesModalOpen(false);
  };

  useEffect(() => {
    dispatch(getAllUnits({ limit: 1000, offset: 0 }));
  }, [dispatch]);
  const contentBeforeSearchFilter = (
    <FilterDropdown
      isDropdownOpen={isDropdownOpen}
      setIsDropdownOpen={setIsDropdownOpen}
      filterType={filterType}
      setFilterType={setFilterType}
      disabledFltrOptions={
        disableBomOptions
          ? [...disabledFilters, "bom_step", "bom_procedure"]
          : disabledFilters
      }
      disableFilter={disableFilter}
      filterOptions={[
        { value: "all", label: "All Items" },
        {
          value: "bom_step",
          label: "Items in BOM for step",
        },
        {
          value: "bom_procedure",
          label: "Items in BOM for Documents",
        },
        { value: "is_module", label: "Items that are Modules" },
        { value: "is_procedure", label: "Items that are Documents" },
        { value: "is_flowchart", label: "Items that are Flowchart" },
        { value: "suggestion_rule_match", label: "Suggestion Rule Match" },
      ]}
    />
  );

  const contentAfterSearchFilter = (
    <CustomColorPicker2
      fieldState={fieldState}
      setFormState={setEditedRowData}
    />
  );

  return (
    <>
      <div>
        {/* <ImportCsvSuccessPopup /> */}
        <AppTable
          ref={tableContainerRef}
          columnsData={
            useTableInAuditTrail
              ? columns
                  .map((d) => ({ ...d, sort: false }))
                  .filter((a) => a.field !== "action")
              : columns
          }
          showSearchInput={useTableInAuditTrail ? false : true}
          contentBeforeSearchFilter={contentBeforeSearchFilter}
          contentAfterSearchFilter={contentAfterSearchFilter}
          paginate={useTableInAuditTrail ? false : true}
          pagination={{
            limit: [limit, setLimit],
            offset: [offset, setOffset],
            pagination,
            maxVsbleButtons: 7,
            paginationPosition: {
              position: "center",
              leftContent: (
                <div style={{ opacity: disableActionButtons ? 0.5 : 1 }}>
                  <AddBtn
                    onClick={() => {
                      if (disableActionButtons) return;
                      // Check if a row is already being edited
                      if (editingRowId !== null) {
                        snackbar(
                          "Please save or cancel the current row before adding or editing another one.",
                          "error"
                        );
                        return;
                      }

                      // Check if a new row is already being added
                      if (newRow !== null) {
                        snackbar(
                          "Please save or cancel the current row before adding or editing another one.",
                          "error"
                        );
                        return;
                      }

                      setNewRow({});
                      setNewPart({
                        type_id_ref: "",
                        unit_type_id_ref: "",
                        part_number: "",
                        description: "",
                        table_note: "",
                        textbox_note: "",
                        default_unit: "",
                        default_unit_show: "",
                        url: "",
                      });
                      setType("");
                      scrollToTop(tableContainerRef);
                    }}
                    disabled={disableActionButtons}
                  />
                </div>
              ),
              rightContent: (
                <div
                  style={{
                    opacity: disableActionButtons ? 0.5 : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                    maxWidth: "100%",
                  }}
                >
                  <UploadViaCsvBtn
                    fileInputRef={fileInputRef}
                    handleFileChange={handleFileChange}
                    disabled={disableActionButtons}
                  />
                  <ExportCsvBtn
                    handleExportCSV={
                      disableActionButtons ? () => {} : handleExportCSV
                    }
                    disabled={disableActionButtons}
                  />
                </div>
              ),
            },
            paginationShadow: true,
            showRecentItems: true,
          }}
          onFilterSortHandler={setSearchQuery}
          usingInPopup={true}
        >
          {!useTableInAuditTrail && (
            <>
              {newRow && (
                <tr>
                  <td></td>
                  <td>
                    <TypesDropdown onChange={handleSelectType} />
                  </td>

                  <td>
                    <input
                      type="text"
                      style={{ height: "25px", fontSize: "13px" }}
                      name="part_number"
                      value={newPart.part_number}
                      onChange={handleInputChange}
                    />
                  </td>
                  <td>
                    <LexicalComposer
                      initialConfig={createEditorConfig("new_description")}
                    >
                      <ColorTextEditor2
                        setFieldState={setFieldState}
                        fieldName="description"
                        setFormState={setNewPart}
                      />
                    </LexicalComposer>
                  </td>
                  <td>
                    <LexicalComposer
                      initialConfig={createEditorConfig("new_table_note")}
                    >
                      <ColorTextEditor2
                        setFieldState={setFieldState}
                        fieldName="table_note"
                        setFormState={setNewPart}
                      />
                    </LexicalComposer>
                  </td>
                  <td>
                    <LexicalComposer
                      initialConfig={createEditorConfig("new_textbox_note")}
                    >
                      <ColorTextEditor2
                        setFieldState={setFieldState}
                        fieldName="textbox_note"
                        setFormState={setNewPart}
                      />
                    </LexicalComposer>
                  </td>
                  <td>
                    {/* <select
                      name="unit_type_id_ref"
                      style={{
                        height: "25px",
                        fontSize: "13px",
                        borderRadius: "5px",
                        border: "1px solid lightgray",
                        width: "100%",
                        paddingLeft:"5px"
                      }}
                      value={newPart.unit_type_id_ref}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>

                      {getUnitOptions()}
                    </select> */}
                    <UnitesDropdown units={units} onChange={setNewPart} />
                  </td>
                  <td>
                    <input
                      type="text"
                      style={{ height: "25px", fontSize: "13px" }}
                      name="url"
                      value={newPart.url}
                      onChange={(e) =>
                        setNewPart({ ...newPart, url: e.target.value })
                      }
                    />
                  </td>

                  <td>
                    <TableActionGroup
                      mode="editing"
                      onSave={handleAddRow}
                      onCancel={() => {
                        setNewRow(null);
                        setNewPart({
                          type_id_ref: "",
                          unit_type_id_ref: "",
                          part_number: "",
                          description: "",
                          table_note: "",
                          textbox_note: "",
                          default_unit: "",
                          default_unit_show: "",
                          url: "",
                        });
                        setType("");
                      }}
                    />
                  </td>
                </tr>
              )}
              {/* Existing Parts */}
              {parts.map((row, index) => {
                let hasReleasedRev = false;

                if (checkRevReleased) {
                  hasReleasedRev = row.procedure_revision_data?.some((rev) =>
                    rev.status_change_logs?.some(
                      (log) =>
                        log.is_latest && log.status_changed_to === "Released"
                    )
                  );
                }

                let typesDropdownType = "D" 
                let isTypeDisabled = false

                if((row?.type_details?.is_module)){
                  if(row.used_in_module){
                    isTypeDisabled = true
                  }else{
                    typesDropdownType = "D"
                  }
                }
                else if((row?.type_details?.is_procedure) && (row?.type_details?.is_flowchart)){
                  if(row.used_in_module_for_flowchart){
                    isTypeDisabled = true
                  }
                  typesDropdownType = "S"
                }
                else if((row?.type_details?.is_procedure) && (!row?.type_details?.is_flowchart)){
                  if(row.used_in_procedure_revision){
                    typesDropdownType = "S"
                  }
                }

                // Check if all usage flags are false
                const isWhereUsedDisabled =
                  !row.use_in_parts_required &&
                  !row.use_in_text_box_content &&
                  !row.used_in_module;

                return (
                  <tr key={row.part_id} data-row-id={row.part_id}>
                    <td style={{ width: "35px" }}>
                      <div
                        className="info-icon"
                        role="button"
                        tabIndex={isWhereUsedDisabled ? -1 : 0}
                        onClick={
                          isWhereUsedDisabled
                            ? undefined
                            : () => setWhereUsedPart(row)
                        }
                        onKeyDown={
                          isWhereUsedDisabled
                            ? undefined
                            : (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setWhereUsedPart(row);
                                }
                              }
                        }
                        style={{
                          cursor: isWhereUsedDisabled
                            ? "not-allowed"
                            : "pointer",
                          opacity: isWhereUsedDisabled ? 0.5 : 1,
                        }}
                      >
                        <span
                          style={{
                            color: isWhereUsedDisabled ? "#6c757d" : "#0D6EFD",
                          }}
                        >
                          i
                        </span>
                      </div>
                    </td>
                    {editingRowId === row.part_id ? (
                      <>
                        <td>
                          <TypesDropdown
                            defaultValue={{
                              value: editedRowData.type_id_ref,
                              label: editedRowData.type_name,
                            }}
                            onChange={handleSelectType}
                            typesDropdownType={typesDropdownType}
                            typeDtls={
                              editedRowData.type_details || row.type_details
                            }
                            isDisabled={isTypeDisabled}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            style={{ height: "25px", fontSize: "13px" }}
                            value={editedRowData.part_number || ""}
                            onChange={(e) =>
                              setEditedRowData({
                                ...editedRowData,
                                part_number: e.target.value,
                              })
                            }
                            readOnly
                            disabled
                          />
                        </td>
                        <td>
                          <LexicalComposer
                            initialConfig={createEditorConfig(
                              `edit_${row.part_id}_description`
                            )}
                          >
                            <ColorTextEditor2
                              setFieldState={setFieldState}
                              prevContent={editedRowData.description || ""}
                              fieldName="description"
                              setFormState={setEditedRowData}
                            />
                          </LexicalComposer>
                        </td>
                        <td>
                          <LexicalComposer
                            initialConfig={createEditorConfig(
                              `edit_${row.part_id}_table_note`
                            )}
                          >
                            <ColorTextEditor2
                              setFieldState={setFieldState}
                              prevContent={editedRowData.table_note || ""}
                              fieldName="table_note"
                              setFormState={setEditedRowData}
                            />
                          </LexicalComposer>
                        </td>
                        <td>
                          <LexicalComposer
                            initialConfig={createEditorConfig(
                              `edit_${row.part_id}_textbox_note`
                            )}
                          >
                            <ColorTextEditor2
                              setFieldState={setFieldState}
                              prevContent={editedRowData.textbox_note || ""}
                              fieldName="textbox_note"
                              setFormState={setEditedRowData}
                            />
                          </LexicalComposer>
                        </td>
                        <td>
                          <UnitesDropdown
                            units={units}
                            defaultValue={
                              editedRowData.unit_type_id_ref !== null
                                ? {
                                    value: editedRowData.unit_type_id_ref,
                                    label: editedRowData.unit_names,
                                  }
                                : undefined
                            }
                            onChange={setEditedRowData}
                            isDisabled={
                              editedRowData.use_in_step_details ||
                              editedRowData.use_in_text_box_content ||
                              editedRowData.use_in_parts_required
                                ? true
                                : false
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            style={{ height: "25px", fontSize: "13px" }}
                            value={editedRowData.url || ""}
                            onChange={(e) =>
                              setEditedRowData({
                                ...editedRowData,
                                url: e.target.value,
                              })
                            }
                          />
                        </td>
                        <td>
                          <TableActionGroup
                            mode="editing"
                            onSave={() => handleSaveEdit(row.part_id)}
                            onCancel={() => {
                              setEditingRowId(null);
                              setEditedRowData({});
                            }}
                          />
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{row.type_details?.name}</td>
                        <td>{row.part_number}</td>
                        <td
                          style={{
                            // overflow: "hidden",
                            // textOverflow: "ellipsis",
                            // whiteSpace: "nowrap",
                            lineHeight: "20px",
                          }}
                          dangerouslySetInnerHTML={{ __html: row.description }}
                        >
                          {/* {row.description} */}
                        </td>
                        <td
                          dangerouslySetInnerHTML={{ __html: row.table_note }}
                        ></td>
                        <td
                          dangerouslySetInnerHTML={{ __html: row.textbox_note }}
                        ></td>
                        <td>
                          {row?.units?.[0]?.unit_name
                            ? row?.units?.[0]?.unit_name
                            : "-"}
                        </td>

                        <td
                          style={{
                            maxWidth: "100px",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                          }}
                        >
                          {row.url && (
                            <a
                              href={row.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {row.url}
                            </a>
                          )}
                        </td>
                        <td>
                          <TableActionGroup
                            mode="view"
                            showEdit={true}
                            showDelete={!onSelectPart}
                            showSelect={!!onSelectPart}
                            disableEdit={
                              disableActions ||
                              newRow !== null ||
                              editingRowId !== null
                            }
                            disableDelete={
                              disableActions ||
                              row.used_in_procedure_revision ||
                              row.used_in_module_for_flowchart ||
                              newRow !== null ||
                              editingRowId !== null ||
                              row.use_in_step_details ||
                              row.use_in_parts_required ||
                              row.use_in_text_box_content ||
                              row.used_in_module
                            }
                            disableSelect={
                              disableActions ||
                              newRow !== null ||
                              editingRowId !== null ||
                              (checkRevCreated &&
                                row.used_in_procedure_revision) ||
                              (checkRevCreatedNot &&
                                !row.used_in_procedure_revision) ||
                              (check_module_created && row.used_in_module) ||
                              (checkRevReleased && !hasReleasedRev)
                            }
                            onEdit={() => {
                              // Check if a row is already being edited
                              if (editingRowId !== null) {
                                snackbar(
                                  "Please save or cancel the current row before adding or editing another one.",
                                  "error"
                                );
                                return;
                              }

                              // Check if a new row is already being added
                              if (newRow !== null) {
                                snackbar(
                                  "Please save or cancel the current row before adding or editing another one.",
                                  "error"
                                );
                                return;
                              }

                              let defaultUnit = units.find(
                                (unit) =>
                                  unit.unit_type_id === row.unit_type_id_ref
                              )?.unit_type_for_name?.[0]?.unit_name;
                              if (!defaultUnit) defaultUnit = "";
                              setEditingRowId(row.part_id);
                              setEditedRowData({
                                ...row,
                                type_name: row.type_details?.name,
                                unit_type_id_ref: row.unit_type_id_ref,
                                default_unit_show: defaultUnit,
                              });
                            }}
                            onDelete={() => {
                              // Check if a row is already being edited
                              if (editingRowId !== null) {
                                snackbar(
                                  "Please save or cancel the current row before adding or editing another one.",
                                  "error"
                                );
                                return;
                              }

                              // Check if a new row is already being added
                              if (newRow !== null) {
                                snackbar(
                                  "Please save or cancel the current row before adding or editing another one.",
                                  "error"
                                );
                                return;
                              }

                              handleDelete(row.part_id);
                            }}
                            onSelect={() => onSelectPart(row)}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </>
          )}

          {useTableInAuditTrail && <>{auditTrailRow}</>}
        </AppTable>
      </div>

      {typesModalOpen && (
        <TypesPopup
          closeTypesModal={setTypesModalOpen}
          onSelectType={handleSelectType}
        />
      )}
      {importedInfo && (
        <ImportCsvSuccessPopup
          importedInfo={importedInfo}
          setNullInportData={setImportData}
        />
      )}

      {showLoader && <Loader />}
      {whereUsedPart && (
        <PartMentionsPopup
          part={whereUsedPart}
          onClose={() => setWhereUsedPart(null)}
          onCloseParentPopup={onCloseParentPopup}
        />
      )}
    </>
  );
};

export default PartsTable;
